import React, { FC } from 'react';
import { Row, Col, Input, DatePicker } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  ApplicationStatusSelect,
  RegionSelect,
  Form,
  LeadStatusSelect,
  AssigneeSelect,
  SalesFilterSelect,
  LeadFilterSelect,
  Button,
} from '~/components';
import { usePath, useAgentMenuItem } from '~/hooks';
import DashboardStatusInput from '~/components/inputs/DashboardStatusInput';
import { DashboardStatus } from '~/types/AgentMapItem';
import DateInputRangeFormItem from '~/components/inputs/DateRangeFormItem';
import { isSales } from '~/utils/getDecodedJwt';
import DownloadReportButtons from './DownloadReportButtons';

const filterOptions = [
  { label: 'UTM-Source', name: 'utmSource' },
  { label: 'UTM-Medium', name: 'utmMedium' },
  { label: 'Campaign Name', name: 'campaignName' },
  { label: 'Ad Group Name', name: 'adGroupName' },
  { label: 'Channel', name: 'channel' },
  { label: 'Product', name: 'product' },
  { label: 'Stage', name: 'stage' },
  { label: 'Assignee', name: 'assignee' },
  { label: 'Result', name: 'result' },
];

const AgentFilters: FC<any> = () => {
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation(['agent', 'ksaReview', 'common']);
  const { changePath, params } = usePath();
  const currentMenuItem = useAgentMenuItem();

  const handleChange = (changed: any, values: Record<string, any>) => {
    changePath({ filter: values });
  };

  const getFilterSelection = (options: typeof filterOptions) =>
    options.map((opt) => {
      // Use LeadFilterSelect for UTM and Campaign filters
      const isLeadFilter = ['utmSource', 'utmMedium', 'campaignName', 'adGroupName', 'stage'].includes(opt.name);

      return (
        <Col>
          <Form.Item label={t(opt.label)} name={opt.name}>
            {opt.name === 'assignee' ? (
              <AssigneeSelect allowClear />
            ) : isLeadFilter ? (
              <LeadFilterSelect filterCriteria={opt.name as any} allowClear />
            ) : (
              <SalesFilterSelect filterCriteria={opt.name} allowClear />
            )}
          </Form.Item>
        </Col>
      );
    });

  if (isSales()) {
    return (
      <StyledForm
        form={form}
        layout="inline"
        onValuesChange={handleChange}
        initialValues={params.filter}
        size="middle"
      >
        <Row gutter={16}>
          {getFilterSelection(filterOptions)}
          <Col>
            <Form.Item name="applicationIdFilter">
              <Input.Search
                allowClear
                placeholder={t('Search by Application ID')}
              />
            </Form.Item>
          </Col>
        </Row>
      </StyledForm>
    );
  }

  return (
    <StyledForm
      form={form}
      // layout="inline"
      onValuesChange={handleChange}
      initialValues={params.filter}
      size="middle"
    >
      <Row gutter={16} align="bottom">
        <Col span={6}>
          <Form.Item name="dashboardStatus" initialValue={DashboardStatus.ALL}>
            <DashboardStatusInput />
          </Form.Item>
        </Col>

        <Col>
          <Form.Item label={t('Status')} name="filterStatus">
            {currentMenuItem === 'leads' ? (
              <LeadStatusSelect allowClear />
            ) : currentMenuItem === 'applications' ? (
              <ApplicationStatusSelect allowClear />
            ) : undefined}
          </Form.Item>
        </Col>

        {currentMenuItem === 'leads' && (
          <Col>
            <Form.Item name="applicationIdFilter">
              <Input.Search
                allowClear
                placeholder={t('Search by Application ID')}
              />
            </Form.Item>
          </Col>
        )}

        <Col>
          <Form.Item label={t('Region')} name="filterRegion">
            <RegionSelect allowClear />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <DateInputRangeFormItem label={t('Created date')} name="filterCreatedDate" />
        </Col>

        <Col xs={24} sm={12} md={6}>
          <DateInputRangeFormItem label={t('Submitted date')} name="filterSubmittedDate" />
        </Col>

        <Col>
          <Form.Item label={t('Assignee')} name="filterAssignee">
            <AssigneeSelect allowClear />
          </Form.Item>
        </Col>

        {currentMenuItem === 'applications' && (
          <>
            <Col xs={24} sm={12} md={12} lg="auto">
              <DownloadReportButtons />
            </Col>
          </>
        )}
      </Row>
    </StyledForm>
  );
};

const StyledForm = styled(Form)`
  margin-top: 24px;
  .ant-form-item {
    min-width: 230px;
  }
  @media (max-width: 768px) {
    .ant-form-item {
      min-width: 100%;
      width: 100%;
    }
  }
`;

export default AgentFilters;
